import { usePrivilege } from '@mews-commander/core';
import { useAppConfiguration, useGraphqlQuery } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { graphql, GuestPhoto, GuestPhotosResultQuery } from '@mews-commander/guest-profile';
import { useFeatureFlag } from '@mews/framework';

const GuestPhotosQueryDocument = graphql(`
    query guestPhotosResult($guestIds: [UUID!]!) {
        guestPhotos(guestIds: $guestIds) {
            id
            createdUtc
            guestId
            name
            imageUrl
        }
    }
`);
interface TransformToSafeGuestPhotosProps {
    guestIds: string[];
    data: GuestPhotosResultQuery;
}
export type GuestPhotos = Record<string, GuestPhoto[]>;

const transformToSafeGuestPhotos = ({ guestIds, data }: TransformToSafeGuestPhotosProps) =>
    guestIds.reduce<GuestPhotos>((result, guestId) => {
        result[guestId] = data.guestPhotos.filter((photo) => photo.guestId === guestId);
        return result;
    }, {});

interface GuestPhotosQueryProps {
    guestIds: string[];
}

export const useGuestsPhotosQuery = ({ guestIds }: GuestPhotosQueryProps) => {
    const { guestProfileApiUrl } = useAppConfiguration();
    const hasPrivilege = usePrivilege('viewCustomerData');
    const guestVerificationPhotoOciConfigurationEnabled = useFeatureFlag(
        FeatureFlag.guestVerificationPhotoOciConfigurationEnabled
    );

    const { data, ...other } = useGraphqlQuery(
        guestProfileApiUrl,
        {
            document: GuestPhotosQueryDocument,
            variables: { guestIds },
        },
        {
            enabled: guestVerificationPhotoOciConfigurationEnabled && hasPrivilege,
            staleTime: 0,
        }
    );

    return { ...other, data: data ? transformToSafeGuestPhotos({ guestIds, data }) : undefined };
};

export const getCustomerVerificationPhoto = (guestId: string, guestPhotos: GuestPhotos | undefined) =>
    guestPhotos?.[guestId]?.[0] ?? null;
